import { alpha, darken, getContrastRatio, lighten } from "@mui/material/styles";

export const PRIMARY_PRESETS = [
  { name: "Dynaship Blue", labelKey: "settings.appearance.primaryColors.dynashipBlue", value: "#3346a8" },
  { name: "Indigo", labelKey: "settings.appearance.primaryColors.indigo", value: "#4f46e5" },
  { name: "Blue", labelKey: "settings.appearance.primaryColors.blue", value: "#2563eb" },
  { name: "Teal", labelKey: "settings.appearance.primaryColors.teal", value: "#0f766e" },
  { name: "Green", labelKey: "settings.appearance.primaryColors.green", value: "#16803c" },
  { name: "Plum", labelKey: "settings.appearance.primaryColors.plum", value: "#8b3bb5" },
];

export const BACKGROUND_PRESETS = [
  { name: "White", labelKey: "settings.appearance.backgrounds.white", value: "#ffffff" },
  { name: "Soft Gray", labelKey: "settings.appearance.backgrounds.softGray", value: "#f7f9fc" },
  { name: "Pale Blue", labelKey: "settings.appearance.backgrounds.paleBlue", value: "#f2f6ff" },
  { name: "Warm Cream", labelKey: "settings.appearance.backgrounds.warmCream", value: "#fff8f0" },
];

const isHex = (value) => typeof value === "string" && /^#[\da-f]{6}$/i.test(value);
const rgbChannels = (color) =>
  [1, 3, 5].map((index) => parseInt(color.slice(index, index + 2), 16)).join(" ");
const isDarkColor = (color) => {
  const [r, g, b] = [1, 3, 5].map((index) => parseInt(color.slice(index, index + 2), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.42;
};
const toHsl = (color) => {
  const [r, g, b] = [1, 3, 5].map((index) => parseInt(color.slice(index, index + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const lightness = (max + min) / 2;
  let hue = 0;
  let saturation = 0;

  if (delta) {
    saturation = delta / (1 - Math.abs(2 * lightness - 1));
    if (max === r) hue = ((g - b) / delta) % 6;
    else if (max === g) hue = (b - r) / delta + 2;
    else hue = (r - g) / delta + 4;
    hue = (hue * 60 + 360) % 360;
  }

  return `${Math.round(hue)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%`;
};

const onColor = (color) =>
  getContrastRatio("#ffffff", color) >= getContrastRatio("#172033", color)
    ? "#ffffff"
    : "#172033";

const accessibleAccent = (color, background, minimumRatio = 4.5) => {
  let result = color;
  const adjust = isDarkColor(background) ? lighten : darken;
  for (let step = 0; step < 8 && getContrastRatio(result, background) < minimumRatio; step += 1) {
    result = adjust(result, 0.14);
  }
  return result;
};

export const DEFAULT_APPEARANCE = Object.freeze({
  mode: "light",
  backgroundColor: "#ffffff",
  primaryColor: "#3346a8",
  shellColorSource: "primary",
  radius: "default",
  density: "comfortable",
  fontSize: "default",
});

const validChoice = (value, choices, fallback) =>
  choices.includes(value) ? value : fallback;

export function normalizeAppearancePreferences(value = {}, legacyMode) {
  const saved = value && typeof value === "object" ? value : {};
  const requestedBackground = typeof saved.backgroundColor === "string" ? saved.backgroundColor.toLowerCase() : "";
  const backgroundColor = isHex(requestedBackground)
    ? requestedBackground
    : DEFAULT_APPEARANCE.backgroundColor;
  const requestedPrimary = typeof saved.primaryColor === "string" ? saved.primaryColor.toLowerCase() : "";
  return {
    mode: validChoice(saved.mode, ["light", "dark", "custom"], validChoice(legacyMode, ["light", "dark"], DEFAULT_APPEARANCE.mode)),
    backgroundColor,
    primaryColor: isHex(requestedPrimary) ? requestedPrimary : DEFAULT_APPEARANCE.primaryColor,
    shellColorSource: validChoice(saved.shellColorSource, ["primary", "background"], DEFAULT_APPEARANCE.shellColorSource),
    radius: validChoice(saved.radius, ["compact", "default", "rounded"], DEFAULT_APPEARANCE.radius),
    density: validChoice(saved.density, ["compact", "comfortable", "spacious"], DEFAULT_APPEARANCE.density),
    fontSize: validChoice(saved.fontSize, ["small", "default", "large"], DEFAULT_APPEARANCE.fontSize),
  };
}

export function getAppearanceTokens(preferences = DEFAULT_APPEARANCE, mode = "light") {
  const isDark = mode === "dark";
  // Color choices are independent from the light/dark mode. Previously the
  // selected preset was discarded unless the whole appearance mode was
  // `custom`, so preset swatches could not tint the application shell.
  const chosenPrimary = isHex(preferences.primaryColor)
    ? preferences.primaryColor
    : DEFAULT_APPEARANCE.primaryColor;
  const primary = isDark && isDarkColor(chosenPrimary)
    ? lighten(chosenPrimary, chosenPrimary.toLowerCase() === "#171717" ? 0.82 : 0.36)
    : !isDark && !isDarkColor(chosenPrimary)
      ? darken(chosenPrimary, 0.55)
      : chosenPrimary;
  const primaryHover = isDark ? lighten(primary, 0.1) : darken(primary, 0.12);
  const primaryActive = isDark ? lighten(primary, 0.18) : darken(primary, 0.2);
  const primaryForeground = onColor(primary);
  const background = isDark ? "#111827" : preferences.mode === "custom" ? preferences.backgroundColor : "#ffffff";
  const pageForeground = isDarkColor(background) ? "#f3f4f6" : "#172033";
  const pageMutedForeground = isDarkColor(background) ? "#cbd5e1" : "#667085";
  const surface = isDark ? "#1f2937" : "#ffffff";
  // A quiet, cool canvas for nested cards and secondary controls. The old
  // mid-grey made repeated cards feel heavy, especially inside white panels.
  const muted = isDark ? "#253247" : "#f8faff";
  const foreground = isDark ? "#f3f4f6" : "#172033";
  const mutedForeground = isDark ? "#c2c8d2" : "#667085";
  const border = isDark ? "#3a475b" : "#e7ebf3";
  const radius = { compact: 6, default: 12, rounded: 16 }[preferences.radius] ?? 12;
  const density = {
    compact: { control: 34, row: 40, gap: 12, card: 16, menu: 38 },
    comfortable: { control: 40, row: 48, gap: 18, card: 22, menu: 46 },
    spacious: { control: 46, row: 56, gap: 24, card: 28, menu: 52 },
  }[preferences.density] ?? { control: 40, row: 48, gap: 18, card: 22, menu: 46 };
  const scale = { small: 0.92, default: 1, large: 1.08 }[preferences.fontSize] ?? 1;
  const focusRing = getContrastRatio(primary, background) >= 3
    ? primary
    : isDark ? lighten(primary, 0.55) : darken(primary, 0.55);
  const accent = primary;
  const destructive = isDark ? "#fb7185" : "#c73b51";
  const chosenBackground = isHex(preferences.backgroundColor)
    ? preferences.backgroundColor
    : DEFAULT_APPEARANCE.backgroundColor;
  // Preset and color-picker values share the same shell tinting path. Do not
  // discard neutral background presets: they should affect the navbar and
  // sidebar with the same lighten/darken percentages as custom colors.
  const shellSeed = preferences.shellColorSource === "background"
    ? chosenBackground
    : chosenPrimary;
  const navbarBackground = isDark
    ? isDarkColor(shellSeed) ? darken(shellSeed, 0.62) : darken(shellSeed, 0.82)
    : isDarkColor(shellSeed) ? lighten(shellSeed, 0.91) : darken(shellSeed, 0.025);
  const sidebarBackground = isDark
    ? isDarkColor(shellSeed) ? darken(shellSeed, 0.54) : darken(shellSeed, 0.78)
    : isDarkColor(shellSeed) ? lighten(shellSeed, 0.9) : darken(shellSeed, 0.035);
  const shellBackground = sidebarBackground;
  const shellForeground = onColor(shellBackground);
  const navbarForeground = onColor(navbarBackground);
  const shellMutedForeground = alpha(shellForeground, 0.72);
  const navbarMutedForeground = alpha(navbarForeground, 0.72);
  const shellBorder = alpha(shellForeground, isDark ? 0.16 : 0.11);
  const navbarBorder = alpha(navbarForeground, isDark ? 0.16 : 0.1);
  const shellHover = alpha(shellForeground, isDark ? 0.11 : 0.07);
  const navbarHover = alpha(navbarForeground, isDark ? 0.11 : 0.065);
  const shellActive = accessibleAccent(primary, shellBackground, 5.5);
  const navbarActive = accessibleAccent(primary, navbarBackground, 5.5);
  const rgb = {
    background: rgbChannels(background), foreground: rgbChannels(foreground),
    card: rgbChannels(surface), primary: rgbChannels(primary),
    primaryForeground: rgbChannels(primaryForeground), secondary: rgbChannels(muted),
    secondaryForeground: rgbChannels(foreground), muted: rgbChannels(muted),
    mutedForeground: rgbChannels(mutedForeground), accent: rgbChannels(muted),
    accentForeground: rgbChannels(foreground), border: rgbChannels(border),
    destructive: rgbChannels(destructive), destructiveForeground: rgbChannels("#ffffff"),
  };

  const vars = {
    "--font-family": 'Inter, "Segoe UI", Tahoma, Arial, sans-serif',
    "--surface-blur": "blur(10px)",
    "--background": background,
    "--page-foreground": pageForeground,
    "--page-muted-foreground": pageMutedForeground,
    "--foreground": foreground,
    "--background-rgb": rgb.background,
    "--foreground-rgb": rgb.foreground,
    "--card-bg": surface,
    "--card-rgb": rgb.card,
    "--card-foreground-rgb": rgb.foreground,
    "--surface": surface,
    "--surface-elevated": isDark ? "#263449" : "#ffffff",
    "--surface-muted": muted,
    "--surface-muted-highlight": isDark
      ? alpha(primary, 0.1)
      : alpha(primary, 0.045),
    "--surface-muted-border": isDark
      ? alpha(lighten(primary, 0.35), 0.18)
      : alpha(primary, 0.12),
    "--surface-alt": muted,
    "--surface-soft": muted,
    "--surface-border": border,
    "--surface-hover": isDark ? "#273449" : "#f6f7fb",
    "--table-header-bg": isDark ? "#1f2937" : "#ffffff",
    "--text-primary": foreground,
    "--text-secondary": mutedForeground,
    "--secondary": mutedForeground,
    "--primary": primary,
    "--primary-rgb": rgb.primary,
    "--primary-foreground-rgb": rgb.primaryForeground,
    "--primary-foreground": primaryForeground,
    "--on-primary": primaryForeground,
    "--primary-hover": primaryHover,
    "--primary-strong": primaryHover,
    "--primary-active": primaryActive,
    "--primary-container": isDark ? alpha(lighten(primary, 0.2), 0.22) : "transparent",
    "--primary-muted": isDark ? alpha(primary, 0.16) : alpha(primary, 0.08),
    "--focus-ring": focusRing,
    "--ring": focusRing,
    "--secondary-rgb": rgb.secondary,
    "--secondary-foreground-rgb": rgb.secondaryForeground,
    "--muted": muted,
    "--muted-rgb": rgb.muted,
    "--muted-foreground": mutedForeground,
    "--muted-foreground-rgb": rgb.mutedForeground,
    "--accent": accent,
    "--accent-rgb": rgb.accent,
    "--accent-foreground-rgb": rgb.accentForeground,
    "--hover-bg": isDark ? "#273449" : "#f6f8fc",
    "--border": border,
    "--border-rgb": rgb.border,
    "--input": border,
    "--input-rgb": rgb.border,
    "--error": destructive,
    "--error-foreground": onColor(destructive),
    "--danger": destructive,
    "--destructive-rgb": rgb.destructive,
    "--destructive-foreground-rgb": rgb.destructiveForeground,
    "--warning": isDark ? "#fbbf24" : "#b7791f",
    "--info": "#0891b2",
    "--highlight": "#7c3aed",
    "--success": isDark ? "#4ade80" : "#24845b",
    "--success-soft": alpha(isDark ? "#4ade80" : "#24845b", isDark ? 0.18 : 0.1),
    "--success-border": alpha(isDark ? "#4ade80" : "#24845b", 0.35),
    "--shadow": isDark ? "0 10px 28px rgba(0,0,0,.28)" : "0 10px 28px rgba(16,24,40,.12)",
    "--shadowx": isDark ? "0 1px 3px rgba(0,0,0,.18)" : "0 1px 3px rgba(16,24,40,.05)",
    "--section-shadow": isDark ? "0 5px 20px rgba(0,0,0,.35)" : "0 5px 20px rgba(15,23,42,.1)",
    "--admin-radius": `${radius}px`,
    "--radius": `${radius}px`,
    "--radius-sm": `${Math.round(radius * 0.5)}px`,
    "--radius-md": `${radius}px`,
    "--radius-lg": `${Math.round(radius * 1.25)}px`,
    "--radius-xl": `${radius * 2}px`,
    "--shadcn-radius": `${radius}px`,
    "--control-height": `${density.control}px`,
    "--row-height": `${density.row}px`,
    "--density-gap": `${density.gap}px`,
    "--spacing-sm": `${Math.round(density.gap * 2 / 3)}px`,
    "--card-padding": `${density.card}px`,
    "--menu-item-height": `${density.menu}px`,
    "--font-scale": scale,
    "--font-size-caption": `calc(.75rem * ${scale})`,
    "--font-size-body": `calc(1rem * ${scale})`,
    "--font-size-subtitle": `calc(1.125rem * ${scale})`,
    "--font-size-heading": `calc(1.5rem * ${scale})`,
    "--font-size-title": `calc(2rem * ${scale})`,
    "--set-btn-bg": primary,
    "--set-btn-text": primaryForeground,
    "--set-btn-border-radius": `${radius}px`,
    "--aside-link-color": shellMutedForeground,
    "--aside-active-color": primary,
    "--aside-active-bg": isDark ? alpha(primary, 0.18) : alpha(primary, 0.08),
    "--aside-divider-color": border,
    "--shell-background": shellBackground,
    "--shell-foreground": shellForeground,
    "--shell-muted-foreground": shellMutedForeground,
    "--shell-border": shellBorder,
    "--shell-hover": shellHover,
    "--shell-active": shellActive,
    "--shell-active-bg": alpha(shellActive, preferences.mode === "custom" ? 0.12 : 0.08),
    "--navbar-background": navbarBackground,
    "--navbar-foreground": navbarForeground,
    "--navbar-muted-foreground": navbarMutedForeground,
    "--navbar-border": navbarBorder,
    "--navbar-hover": navbarHover,
    "--navbar-active": navbarActive,
    "--navbar-active-bg": alpha(navbarActive, isDark ? 0.18 : 0.1),
    "--sidebar-background": sidebarBackground,
    "--sidebar-foreground": shellForeground,
    "--sidebar-muted-foreground": shellMutedForeground,
    "--sidebar-border": shellBorder,
    "--sidebar-hover": shellHover,
    "--sidebar-active": shellActive,
    "--sidebar-active-bg": alpha(shellActive, isDark ? 0.18 : 0.1),
    "--shadcn-background": toHsl(background),
    "--shadcn-foreground": toHsl(foreground),
    "--shadcn-card": toHsl(surface),
    "--shadcn-card-foreground": toHsl(foreground),
    "--shadcn-popover": toHsl(surface),
    "--shadcn-popover-foreground": toHsl(foreground),
    "--shadcn-primary": toHsl(primary),
    "--shadcn-primary-foreground": toHsl(primaryForeground),
    "--shadcn-secondary": toHsl(muted),
    "--shadcn-secondary-foreground": toHsl(foreground),
    "--shadcn-muted": toHsl(muted),
    "--shadcn-muted-foreground": toHsl(mutedForeground),
    "--shadcn-accent": toHsl(muted),
    "--shadcn-accent-foreground": toHsl(foreground),
    "--shadcn-border": toHsl(border),
    "--shadcn-input": toHsl(border),
    "--shadcn-ring": toHsl(focusRing),
    "--shadcn-sidebar-background": toHsl(sidebarBackground),
    "--shadcn-sidebar-foreground": toHsl(shellForeground),
    "--shadcn-sidebar-primary": toHsl(shellActive),
    "--shadcn-sidebar-primary-foreground": toHsl(onColor(shellActive)),
    "--shadcn-sidebar-accent": toHsl(sidebarBackground),
    "--shadcn-sidebar-accent-foreground": toHsl(shellForeground),
    "--shadcn-sidebar-border": toHsl(sidebarBackground),
    "--shadcn-sidebar-ring": toHsl(focusRing),
  };

  return {
    vars,
    colors: { primary, primaryHover, primaryActive, primaryForeground, accent, destructive, surface, background, muted, foreground, mutedForeground, border },
    radius,
    density,
    scale,
  };
}
